/**
 * Delete confirm for hub uploads — filename, ledger/Vault impact, Cancel focused.
 */
import { useEffect, useRef } from 'react'

export default function HubDocDeleteConfirm({
  fileName,
  body,
  subSentLine,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  fileName: string
  body: string
  subSentLine?: string | null
  busy?: boolean
  error?: string | null
  onCancel: () => void
  onConfirm: () => void
}) {
  const cancelRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    cancelRef.current?.focus()
  }, [])

  return (
    <div
      data-testid="hub-doc-delete-confirm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hub-doc-delete-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={() => {
        if (!busy) onCancel()
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r)',
          padding: 24,
          maxWidth: 400,
          width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="hub-doc-delete-title" style={{ margin: '0 0 10px', fontSize: 15 }}>
          Delete {fileName}?
        </h3>
        <p
          data-testid="hub-doc-delete-body"
          style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--ink3)', lineHeight: 1.45 }}
        >
          {body}
        </p>
        {subSentLine ? (
          <p
            data-testid="hub-doc-delete-sub-sent"
            style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--ink3)', lineHeight: 1.45 }}
          >
            {subSentLine}
          </p>
        ) : (
          <div style={{ marginBottom: 16 }} />
        )}
        {error && (
          <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--red)' }}>{error}</p>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            ref={cancelRef}
            className="btn-ghost"
            type="button"
            data-testid="hub-doc-delete-cancel"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="hub-doc-delete-confirm-btn"
            disabled={busy}
            onClick={onConfirm}
            style={{
              background: 'var(--red)',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--r)',
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? 'Removing…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}
